import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Scale, Lock, UserRound, ArrowRight, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LAWYER_BASE } from "@lawyer/constants/route";

export default function LawyerLogin() {
  const { user, login, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || LAWYER_BASE;
  const authError = location.state?.auth_error;
  const lawyerGate = location.state?.lawyer_gate; // unregistered | pending | rejected
  const isNonLawyer = !loading && user && user.role !== "lawyer";

  useEffect(() => {
    if (loading || !user) return;
    if (user.role === "lawyer") navigate(from, { replace: true });
    // Non-lawyers stay on this page and get an honest explanation below.
  }, [user, loading, navigate, from]);

  // Status-aware result of a Google sign-in attempt (backend-verified email).
  if (lawyerGate) {
    const copy = {
      unregistered: {
        title: "Lawyer account not found",
        body: "We couldn't find a lawyer application associated with this Google account. Please register as a lawyer first.",
        cta: { label: "Register as a Lawyer", href: "/become-a-lawyer", testid: "gate-register-btn" },
      },
      pending: {
        title: "Application pending",
        body: "Your lawyer application is currently under review. You'll be able to access the Lawyer Portal once your application is approved.",
        note: "Approval usually takes 1–2 working days — we'll notify you by email and SMS.",
      },
      rejected: {
        title: "Application rejected",
        body: "Your lawyer application was not approved. Please review the rejection reason (sent to your registered email) and resubmit your application if eligible.",
        cta: { label: "Review & Resubmit", href: "/become-a-lawyer", testid: "gate-resubmit-btn" },
      },
    }[lawyerGate];
    if (copy) {
      return (
        <div className="min-h-screen bg-[#0A2540] grid place-items-center p-6">
          <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl" data-testid={`lawyer-login-gate-${lawyerGate}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-xl bg-[#FBF4DF] grid place-items-center text-[#8a6d14]">
                <Scale size={22} />
              </div>
              <div>
                <p className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Advoset Lawyer</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Partner Portal</p>
              </div>
            </div>
            <h1 className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{copy.title}</h1>
            <p className="mt-2 text-sm text-slate-500">{copy.body}</p>
            {copy.note && <p className="mt-3 text-xs text-slate-400">{copy.note}</p>}
            {copy.cta && (
              <a href={copy.cta.href} className="block mt-5">
                <Button className="w-full rounded-xl adv-btn-primary font-bold h-11" data-testid={copy.cta.testid}>
                  {copy.cta.label} <ArrowRight size={14} />
                </Button>
              </a>
            )}
            <button
              onClick={() => navigate("/lawyer-portal/login", { replace: true, state: {} })}
              className="mt-3 w-full h-10 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50"
              data-testid="gate-back-to-login-btn"
            >
              Back to sign in
            </button>
          </div>
        </div>
      );
    }
  }

  if (isNonLawyer) {
    return (
      <div className="min-h-screen bg-[#0A2540] grid place-items-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl" data-testid="lawyer-login-not-lawyer-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-xl bg-[#FBF4DF] grid place-items-center text-[#8a6d14]">
              <UserRound size={22} />
            </div>
            <div>
              <p className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Advoset Lawyer</p>
              <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Partner Portal</p>
            </div>
          </div>
          <h1 className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            This account isn't registered as a lawyer
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            You're signed in as <b className="text-slate-700">{user.email || user.name}</b>, which is a
            client account. To practice on Advoset, apply once and our team will verify and onboard you.
          </p>
          <a href="/become-a-lawyer" className="block mt-5">
            <Button className="w-full rounded-xl adv-btn-primary font-bold h-11" data-testid="apply-to-join-btn">
              <Scale size={16} /> Apply to join as a lawyer <ArrowRight size={14} />
            </Button>
          </a>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <a href="/">
              <Button variant="outline" className="w-full rounded-xl h-10 text-xs font-bold" data-testid="continue-as-client-btn">
                Continue as client
              </Button>
            </a>
            <Button
              variant="outline"
              onClick={logout}
              className="w-full rounded-xl h-10 text-xs font-bold"
              data-testid="switch-account-btn"
            >
              <LogOut size={13} /> Use another account
            </Button>
          </div>
          <p className="mt-4 text-[11px] text-slate-400">
            Already applied? Approval usually takes 1–2 working days — you'll get an email when you're in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A2540] grid place-items-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl" data-testid="lawyer-login-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-[#D4AF37] grid place-items-center text-[#0A2540]">
            <Scale size={22} />
          </div>
          <div>
            <p className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Advoset Lawyer</p>
            <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Partner Portal</p>
          </div>
        </div>

        <h1 className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Welcome back, advocate</h1>
        <p className="mt-2 text-sm text-slate-500">
          Sign in with your registered email or phone. Only verified lawyers can access this portal.
        </p>

        {authError && (
          <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-left" data-testid="lawyer-login-error">
            <p className="text-xs font-bold text-amber-800">{authError}</p>
            <p className="mt-1 text-[11px] text-amber-700">
              Not a registered lawyer yet?{" "}
              <a href="/become-a-lawyer" className="font-bold underline">Apply to join</a> — takes 5 minutes.
            </p>
          </div>
        )}

        <Button
          onClick={login}
          data-testid="lawyer-login-btn"
          className="mt-6 w-full rounded-xl adv-btn-primary font-bold h-11"
        >
          <Scale size={16} /> Sign in to your practice
        </Button>

        <p className="mt-5 text-[11px] text-slate-400 inline-flex items-center gap-1">
          <Lock size={12} /> Secure session · Audit logged
        </p>
        <p className="mt-3 text-[11px] text-slate-500">
          New here? <a href="/become-a-lawyer" className="font-bold text-[#0A2540] underline">Apply to join Advoset</a> and we'll get you onboarded.
        </p>
      </div>
    </div>
  );
}
